/**
 * The transport deadline, alone in its own leaf module.
 *
 * Two modules need it and neither can own it: `axiosInstance` imports
 * `shared/auth/tokenRefresh`, so the refresh transport cannot import back from
 * the instance. `shared/api/config` would break the cycle and is the obvious
 * home — but ~28 suites hand-mock that module with a literal stub object
 * (`vi.mock('.../shared/api/config', () => ({ isCapacitor, API_BASE_URL }))`),
 * so every new export there reads back `undefined` under all of them. For a
 * timeout that is not a loud failure but a silent one: `setTimeout(fn,
 * undefined)` clamps to **0ms**, so every request in those suites would race a
 * deadline that had already expired, passing or failing on microtask timing. A
 * single-purpose leaf nobody mocks avoids both traps, and
 * `shared/api/__tests__/fetchDeadlineUnderConfigMock.test.ts` is what stops it
 * drifting back.
 */

/**
 * Deadline for every request the app makes. Without one, the transport waits
 * forever on a connection the server accepted and then stalled on (a backend
 * restarting mid-request, a wedged dev proxy), so the caller's `finally` never
 * runs and the UI spins with no error and no way out — which is exactly how the
 * /setup wizard hung, and one screen earlier how the Sign In button hung. It
 * also made `errors:TIMEOUT` unreachable: `getErrorMessage` translates axios
 * `ECONNABORTED` and the fetch-side `TimeoutError` (`shared/utils/errors.ts`),
 * and only a deadline produces either, so the documented timeout branch could
 * never fire.
 *
 * 120s sits deliberately ABOVE the 60s CloudFront origin cap, so in production
 * the gateway's own 504 — which carries a real status and error body — always
 * wins the race. This is the backstop for a transport that never answers at all,
 * not a performance budget: a handler that needs longer than 60s is a 504 to
 * fix, not a timeout to raise. Genuinely long-running calls (demo-tenant
 * seeding) opt out per-request with `{ timeout: ... }`.
 *
 * See `docs/platform/error-handling-design.md` § 3.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 120_000;
